// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../AuthContext";
import { supabase } from "../supabase";

// ── Valuation model inputs & multipliers ────────────────────────
const INDUSTRY_MULTIPLIERS = {
  "Agriculture (CropPatrol)": { rev: 6.5, users: 2800 },
  "Fleet Management (Forge Fleet)": { rev: 7.2, users: 3200 },
  "Oil & Gas (Directional Ops)": { rev: 8.0, users: 4500 },
  "Automotive (LotSync)": { rev: 6.0, users: 2500 },
  "Healthcare (NurseVault)": { rev: 9.5, users: 5000 },
  "Healthcare CA (NurseVault CA)": { rev: 9.5, users: 5000 },
  "Service Trades (Vantage Ops)": { rev: 5.5, users: 2200 },
  "Cannabis (BudLedger)": { rev: 7.0, users: 3000 },
  "Mining (OreSync)": { rev: 7.5, users: 3800 },
  "Vac Hauling (VacTraq)": { rev: 6.8, users: 3000 },
  "Beauty (GlowDesk)": { rev: 5.0, users: 2000 },
  "Real Estate (ClosingDesk)": { rev: 6.5, users: 2800 },
  "Mechanic (WrenchSync)": { rev: 5.5, users: 2200 },
  "Tire Shop (TreadOps)": { rev: 5.5, users: 2200 },
  "Cattle (HerdLedger)": { rev: 6.0, users: 2500 },
  "Wellness (VitaSync)": { rev: 5.0, users: 2000 },
  "Insurance (PolicyForge)": { rev: 7.0, users: 3500 },
  "Events (StageVault)": { rev: 5.5, users: 2200 },
  "Forms/Docs (FormGenius)": { rev: 8.5, users: 4000 },
  "Internal Platform (MT Forge)": { rev: 10.0, users: 5000 },
  "Hoist & Crane (LiftForge)": { rev: 6.0, users: 2500 },
  "Rig Safety (RigForge)": { rev: 7.0, users: 3500 },
  "Towing (TowForge)": { rev: 5.5, users: 2200 },
  "Country Club (FRCC)": { rev: 5.0, users: 2000 },
  "AI Pet Care (Forge Companion)": { rev: 6.0, users: 2500 },
  "Blockchain (FlorigenNFT)": { rev: 8.0, users: 4000 },
  "Iron & Steel (IronLedger)": { rev: 6.0, users: 2500 },
  "Grocery (AisleSync)": { rev: 5.5, users: 2200 },
  "Restaurant (PlateSync)": { rev: 5.5, users: 2200 },
  "Overhead Doors (IOD)": { rev: 6.0, users: 2500 },
};

const VALUATION_FACTORS = [
  { key: "ip_portfolio", label: "IP Portfolio (37 apps + patents pending)", weight: 0.25 },
  { key: "market_coverage", label: "Multi-vertical Market Coverage (30 industries)", weight: 0.20 },
  { key: "tech_stack", label: "Technology Stack & Architecture", weight: 0.15 },
  { key: "revenue_potential", label: "Revenue Potential (SaaS recurring)", weight: 0.15 },
  { key: "team_talent", label: "Team & Talent (19 members)", weight: 0.10 },
  { key: "brand_assets", label: "Brand Assets & Domain Portfolio", weight: 0.08 },
  { key: "market_timing", label: "Market Timing & Industry Tailwinds", weight: 0.07 },
];

const DEFAULT_METRICS = {
  total_apps: 37,
  mobile_apps: 25,
  active_users: 0,
  monthly_revenue: 0,
  arr_projection: 0,
  patent_count: 1,
  patent_pending: 3,
  industries_covered: 20,
  team_size: 19,
  code_lines: 450000,
  uptime_percent: 99.87,
};

const formatCurrency = (n) => {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
};

const formatDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export default function ValuationPage() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(DEFAULT_METRICS);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editMetrics, setEditMetrics] = useState({});
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState("");
  const [showBreakdown, setShowBreakdown] = useState(true);
  const [selectedFactor, setSelectedFactor] = useState(null);

  // ── Calculate valuation ────────────────────────────────────────
  const calculateValuation = useCallback((m) => {
    // Method 1: Revenue multiple (ARR × blended multiplier)
    const blendedMultiple = 7.2; // avg SaaS multi-vertical
    const revenueVal = (m.arr_projection || m.monthly_revenue * 12) * blendedMultiple;

    // Method 2: Per-app IP value
    const avgAppValue = 850000; // $850K per vertical SaaS app
    const ipVal = m.total_apps * avgAppValue + m.mobile_apps * 320000;

    // Method 3: Market-size approach (TAM penetration)
    const tamPerIndustry = 2.5e9; // $2.5B avg TAM per vertical
    const penetrationRate = 0.0002; // early stage
    const marketVal = m.industries_covered * tamPerIndustry * penetrationRate;

    // Method 4: Cost-to-recreate
    const costPerLine = 45; // $45/line fully loaded
    const recreateVal = m.code_lines * costPerLine + m.team_size * 180000;

    // Weighted blend
    const totalVal = revenueVal * 0.15 + ipVal * 0.35 + marketVal * 0.25 + recreateVal * 0.25;

    // Patent premium (+15% for pending patents)
    const patentPremium = m.patent_pending > 0 ? 1.15 : 1.0;

    return {
      total: Math.round(totalVal * patentPremium),
      revenueVal: Math.round(revenueVal),
      ipVal: Math.round(ipVal),
      marketVal: Math.round(marketVal),
      recreateVal: Math.round(recreateVal),
      patentPremium,
      methods: [
        { label: "Revenue Multiple", value: revenueVal, weight: "15%", desc: `ARR × ${blendedMultiple}x` },
        { label: "IP Portfolio Value", value: ipVal, weight: "35%", desc: `${m.total_apps} web + ${m.mobile_apps} mobile apps` },
        { label: "Market Size (TAM)", value: marketVal, weight: "25%", desc: `${m.industries_covered} industries × $2.5B TAM` },
        { label: "Cost to Recreate", value: recreateVal, weight: "25%", desc: `${(m.code_lines/1000).toFixed(0)}K lines + team` },
      ],
    };
  }, []);

  const valuation = calculateValuation(metrics);

  // ── Load from Supabase ─────────────────────────────────────────
  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line

  const loadData = async () => {
    setLoading(true);
    try {
      // Load latest metrics
      const { data: latest } = await supabase
        .from("valuation_snapshots")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(1);

      if (latest && latest.length > 0) {
        const m = latest[0].metrics || {};
        setMetrics({ ...DEFAULT_METRICS, ...m });
        setNotes(latest[0].notes || "");
      }

      // Load history (last 90 days)
      const { data: hist } = await supabase
        .from("valuation_snapshots")
        .select("id, created_at, total_valuation, notes, change_reasons")
        .order("created_at", { ascending: false })
        .limit(90);

      if (hist) setHistory(hist);
    } catch (e) {
      console.error("Failed to load valuation data:", e);
    } finally {
      setLoading(false);
    }
  };

  // ── Save snapshot ──────────────────────────────────────────────
  const saveSnapshot = async () => {
    setSaving(true);
    const val = calculateValuation(editMode ? editMetrics : metrics);
    const prevVal = history.length > 0 ? history[0].total_valuation : val.total;
    const changePercent = prevVal > 0 ? ((val.total - prevVal) / prevVal * 100).toFixed(2) : 0;
    const changeReasons = generateChangeReasons(
      editMode ? editMetrics : metrics,
      history.length > 0 && history[0].metrics ? history[0].metrics : DEFAULT_METRICS
    );

    try {
      await supabase.from("valuation_snapshots").insert({
        total_valuation: val.total,
        metrics: editMode ? editMetrics : metrics,
        notes,
        change_percent: parseFloat(changePercent),
        change_reasons: changeReasons,
        recorded_by: user?.email,
        created_at: new Date().toISOString(),
      });

      if (editMode) {
        setMetrics(editMetrics);
        setEditMode(false);
      }
      await loadData();
    } catch (e) {
      console.error("Save failed:", e);
    } finally {
      setSaving(false);
    }
  };

  // ── Auto-generate change reasons ──────────────────────────────
  const generateChangeReasons = (current, previous) => {
    const reasons = [];
    if (current.total_apps !== previous.total_apps) {
      const diff = current.total_apps - previous.total_apps;
      reasons.push(diff > 0 ? `+${diff} new web app(s) launched` : `${diff} app(s) removed`);
    }
    if (current.mobile_apps !== previous.mobile_apps) {
      const diff = current.mobile_apps - previous.mobile_apps;
      reasons.push(diff > 0 ? `+${diff} new mobile app(s)` : `${diff} mobile app(s) removed`);
    }
    if (current.active_users !== previous.active_users) {
      const diff = current.active_users - previous.active_users;
      reasons.push(diff > 0 ? `User base grew by ${diff}` : `User base decreased by ${Math.abs(diff)}`);
    }
    if (current.monthly_revenue !== previous.monthly_revenue) {
      const diff = current.monthly_revenue - previous.monthly_revenue;
      reasons.push(diff > 0 ? `Revenue increased by ${formatCurrency(diff)}/mo` : `Revenue decreased by ${formatCurrency(Math.abs(diff))}/mo`);
    }
    if (current.patent_pending !== previous.patent_pending) {
      reasons.push(`Patent portfolio updated (${current.patent_pending} pending)`);
    }
    if (current.team_size !== previous.team_size) {
      const diff = current.team_size - previous.team_size;
      reasons.push(diff > 0 ? `Team expanded by ${diff}` : `Team reduced by ${Math.abs(diff)}`);
    }
    if (current.code_lines !== previous.code_lines) {
      const diff = current.code_lines - previous.code_lines;
      reasons.push(diff > 0 ? `+${(diff/1000).toFixed(0)}K lines of code added` : `${(diff/1000).toFixed(0)}K lines removed (optimization)`);
    }
    if (current.industries_covered !== previous.industries_covered) {
      const diff = current.industries_covered - previous.industries_covered;
      reasons.push(diff > 0 ? `Expanded to ${diff} new industry vertical(s)` : `Consolidated ${Math.abs(diff)} vertical(s)`);
    }
    if (reasons.length === 0) reasons.push("Daily valuation snapshot — no metric changes");
    return reasons;
  };

  // ── Trend data ─────────────────────────────────────────────────
  const recentChange = history.length >= 2
    ? ((history[0].total_valuation - history[1].total_valuation) / history[1].total_valuation * 100).toFixed(2)
    : 0;
  const weekChange = history.length >= 7
    ? ((history[0].total_valuation - history[6].total_valuation) / history[6].total_valuation * 100).toFixed(2)
    : 0;
  const monthChange = history.length >= 30
    ? ((history[0].total_valuation - history[29].total_valuation) / history[29].total_valuation * 100).toFixed(2)
    : 0;

  const cardStyle = { background: "#fff", borderRadius: 12, padding: "20px 24px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", border: "1px solid #e2e8f0" };
  const labelStyle = { fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 4 };
  const bigNumStyle = { fontSize: 32, fontWeight: 800, color: "#0f172a", lineHeight: 1.1 };

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: "center" }}>
        <div className="loading-spinner" />
        <p style={{ color: "#64748b", marginTop: 16 }}>Loading valuation data...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px 32px", maxWidth: 1400, margin: "0 auto" }}>
      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: "#0f172a", margin: 0 }}>Company Valuation</h1>
          <p style={{ fontSize: 14, color: "#64748b", margin: "4px 0 0" }}>
            Mt Forge Inc. — Daily Valuation Tracker
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {!editMode ? (
            <button onClick={() => { setEditMetrics({ ...metrics }); setEditMode(true); }} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#334155" }}>
              Update Metrics
            </button>
          ) : (
            <>
              <button onClick={() => setEditMode(false)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", fontSize: 13, cursor: "pointer", color: "#64748b" }}>
                Cancel
              </button>
              <button onClick={saveSnapshot} disabled={saving} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#3B82F6", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#fff", opacity: saving ? 0.6 : 1 }}>
                {saving ? "Saving..." : "Save Snapshot"}
              </button>
            </>
          )}
          {!editMode && (
            <button onClick={saveSnapshot} disabled={saving} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#10b981", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#fff", opacity: saving ? 0.6 : 1 }}>
              {saving ? "Saving..." : "Record Daily Snapshot"}
            </button>
          )}
        </div>
      </div>

      {/* ── Headline Valuation Card ───────────────────── */}
      <div style={{ ...cardStyle, marginBottom: 24, background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", color: "#fff", padding: "32px 40px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ ...labelStyle, color: "#94a3b8" }}>Estimated Company Valuation</div>
            <div style={{ ...bigNumStyle, fontSize: 48, color: "#fff" }}>{formatCurrency(valuation.total)}</div>
            <div style={{ marginTop: 8, display: "flex", gap: 16 }}>
              <span style={{ fontSize: 13, color: parseFloat(recentChange) >= 0 ? "#34d399" : "#f87171" }}>
                {parseFloat(recentChange) >= 0 ? "+" : ""}{recentChange}% today
              </span>
              <span style={{ fontSize: 13, color: parseFloat(weekChange) >= 0 ? "#34d399" : "#f87171" }}>
                {parseFloat(weekChange) >= 0 ? "+" : ""}{weekChange}% 7d
              </span>
              <span style={{ fontSize: 13, color: parseFloat(monthChange) >= 0 ? "#34d399" : "#f87171" }}>
                {parseFloat(monthChange) >= 0 ? "+" : ""}{monthChange}% 30d
              </span>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>Patent Premium</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#fbbf24" }}>
              {valuation.patentPremium > 1 ? `+${((valuation.patentPremium - 1) * 100).toFixed(0)}%` : "None"}
            </div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 8 }}>Last Updated</div>
            <div style={{ fontSize: 13, color: "#e2e8f0" }}>
              {history.length > 0 ? formatDate(history[0].created_at) : "No snapshots yet"}
            </div>
          </div>
        </div>
      </div>

      {/* ── Key Metrics Row ───────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 24 }}>
        {[
          { label: "Web Apps", value: metrics.total_apps, icon: "💻" },
          { label: "Mobile Apps", value: metrics.mobile_apps, icon: "📱" },
          { label: "Industries", value: metrics.industries_covered, icon: "🏭" },
          { label: "Team Size", value: metrics.team_size, icon: "👥" },
          { label: "Active Users", value: metrics.active_users.toLocaleString(), icon: "👤" },
          { label: "Monthly Rev", value: formatCurrency(metrics.monthly_revenue), icon: "💰" },
          { label: "ARR Projection", value: formatCurrency(metrics.arr_projection || metrics.monthly_revenue * 12), icon: "📈" },
          { label: "Patents", value: `${metrics.patent_count} + ${metrics.patent_pending} pending`, icon: "📋" },
        ].map((m, i) => (
          <div key={i} style={cardStyle}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>{m.icon}</div>
            <div style={labelStyle}>{m.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* ── Edit Metrics Panel ────────────────────────── */}
      {editMode && (
        <div style={{ ...cardStyle, marginBottom: 24, background: "#fffbeb", borderColor: "#fbbf24" }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: "#92400e" }}>Update Company Metrics</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
            {[
              { key: "total_apps", label: "Web Apps", type: "number" },
              { key: "mobile_apps", label: "Mobile Apps", type: "number" },
              { key: "active_users", label: "Active Users", type: "number" },
              { key: "monthly_revenue", label: "Monthly Revenue ($)", type: "number" },
              { key: "arr_projection", label: "ARR Projection ($)", type: "number" },
              { key: "patent_count", label: "Granted Patents", type: "number" },
              { key: "patent_pending", label: "Patents Pending", type: "number" },
              { key: "industries_covered", label: "Industries Covered", type: "number" },
              { key: "team_size", label: "Team Size", type: "number" },
              { key: "code_lines", label: "Lines of Code", type: "number" },
              { key: "uptime_percent", label: "Uptime %", type: "number" },
            ].map(field => (
              <div key={field.key}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#334155", display: "block", marginBottom: 4 }}>{field.label}</label>
                <input
                  type={field.type}
                  value={editMetrics[field.key] || ""}
                  onChange={(e) => setEditMetrics(prev => ({ ...prev, [field.key]: field.type === "number" ? parseFloat(e.target.value) || 0 : e.target.value }))}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, boxSizing: "border-box" }}
                />
              </div>
            ))}
          </div>
          <div style={{ marginTop: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#334155", display: "block", marginBottom: 4 }}>Notes / Context</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What changed today? (e.g., launched new app, signed new client, filed patent...)"
              rows={3}
              style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 13, resize: "vertical", boxSizing: "border-box" }}
            />
          </div>
          <div style={{ marginTop: 16, padding: 16, background: "#fff", borderRadius: 8, border: "1px solid #e5e7eb" }}>
            <div style={labelStyle}>Preview — New Valuation</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#0f172a" }}>{formatCurrency(calculateValuation(editMetrics).total)}</div>
            <div style={{ fontSize: 12, color: valuation.total < calculateValuation(editMetrics).total ? "#10b981" : "#ef4444", marginTop: 4 }}>
              {formatCurrency(calculateValuation(editMetrics).total - valuation.total)} change from current
            </div>
          </div>
        </div>
      )}

      {/* ── Valuation Breakdown Toggle ────────────────── */}
      <div style={{ marginBottom: 24 }}>
        <button onClick={() => setShowBreakdown(!showBreakdown)} style={{ background: "none", border: "none", fontSize: 16, fontWeight: 700, color: "#0f172a", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 8 }}>
          {showBreakdown ? "▼" : "▶"} Valuation Breakdown
        </button>
      </div>

      {showBreakdown && (
        <>
          {/* ── 4-Method Breakdown ────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginBottom: 24 }}>
            {valuation.methods.map((method, i) => {
              const colors = ["#3B82F6", "#8B5CF6", "#10b981", "#f59e0b"];
              return (
                <div key={i} style={{ ...cardStyle, borderLeft: `4px solid ${colors[i]}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <div style={labelStyle}>{method.label}</div>
                      <div style={{ fontSize: 24, fontWeight: 700, color: "#0f172a" }}>{formatCurrency(method.value)}</div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: colors[i], background: `${colors[i]}15`, padding: "4px 8px", borderRadius: 6 }}>
                      {method.weight}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 8 }}>{method.desc}</div>
                </div>
              );
            })}
          </div>

          {/* ── Per-App IP Breakdown ──────────────────── */}
          <div style={{ ...cardStyle, marginBottom: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Per-App IP Valuation</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 8 }}>
              {Object.entries(INDUSTRY_MULTIPLIERS).map(([industry, mult], i) => {
                const appRevShare = (metrics.monthly_revenue * 12) / metrics.total_apps;
                const appVal = appRevShare * mult.rev + mult.users * 120;
                return (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: i % 2 === 0 ? "#f8fafc" : "#fff", borderRadius: 6 }}>
                    <span style={{ fontSize: 13, color: "#334155" }}>{industry}</span>
                    <div style={{ textAlign: "right" }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{formatCurrency(appVal)}</span>
                      <span style={{ fontSize: 11, color: "#64748b", marginLeft: 8 }}>{mult.rev}x rev</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Valuation Factors ─────────────────────── */}
          <div style={{ ...cardStyle, marginBottom: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Value Driver Weights</h3>
            {VALUATION_FACTORS.map((factor, i) => (
              <div key={i} style={{ marginBottom: 12, cursor: "pointer" }} onClick={() => setSelectedFactor(selectedFactor === i ? null : i)}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>{factor.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#3B82F6" }}>{(factor.weight * 100).toFixed(0)}%</span>
                </div>
                <div style={{ height: 8, background: "#e2e8f0", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ width: `${factor.weight * 100}%`, height: "100%", background: "linear-gradient(90deg, #3B82F6, #8B5CF6)", borderRadius: 4, transition: "width 0.3s" }} />
                </div>
                {selectedFactor === i && (
                  <div style={{ marginTop: 8, padding: 12, background: "#f1f5f9", borderRadius: 8, fontSize: 12, color: "#475569" }}>
                    Weight: {(factor.weight * 100).toFixed(0)}% of total valuation model. This factor contributes approximately {formatCurrency(valuation.total * factor.weight)} to the total company valuation.
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── History / Timeline ────────────────────────── */}
      <div style={{ ...cardStyle }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Valuation History</h3>
        {history.length === 0 ? (
          <div style={{ textAlign: "center", padding: 32, color: "#94a3b8" }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>📊</div>
            <p style={{ fontSize: 14 }}>No snapshots recorded yet.</p>
            <p style={{ fontSize: 12 }}>Click "Record Daily Snapshot" to start tracking.</p>
          </div>
        ) : (
          <div style={{ maxHeight: 500, overflowY: "auto" }}>
            {history.map((entry, i) => {
              const prevVal = history[i + 1]?.total_valuation || entry.total_valuation;
              const change = entry.total_valuation - prevVal;
              const changePercent = prevVal > 0 ? ((change / prevVal) * 100).toFixed(2) : 0;
              const isUp = change >= 0;
              return (
                <div key={entry.id} style={{ display: "flex", gap: 16, padding: "12px 0", borderBottom: i < history.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                  <div style={{ minWidth: 90, textAlign: "right" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>{formatDate(entry.created_at)}</div>
                  </div>
                  <div style={{ width: 3, background: isUp ? "#10b981" : change === 0 ? "#e2e8f0" : "#ef4444", borderRadius: 2, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <span style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>{formatCurrency(entry.total_valuation)}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: isUp ? "#10b981" : change === 0 ? "#94a3b8" : "#ef4444" }}>
                        {isUp && change > 0 ? "+" : ""}{changePercent}% ({isUp && change > 0 ? "+" : ""}{formatCurrency(change)})
                      </span>
                    </div>
                    {entry.change_reasons && entry.change_reasons.length > 0 && (
                      <div style={{ marginTop: 4 }}>
                        {entry.change_reasons.map((reason, ri) => (
                          <span key={ri} style={{ display: "inline-block", fontSize: 11, color: "#64748b", background: "#f1f5f9", padding: "2px 8px", borderRadius: 4, marginRight: 4, marginBottom: 2 }}>
                            {reason}
                          </span>
                        ))}
                      </div>
                    )}
                    {entry.notes && <div style={{ fontSize: 12, color: "#64748b", marginTop: 4, fontStyle: "italic" }}>{entry.notes}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── SQL Setup Notice ──────────────────────────── */}
      {history.length === 0 && (
        <div style={{ marginTop: 16, padding: 16, background: "#fef3c7", borderRadius: 8, border: "1px solid #fbbf24", fontSize: 12, color: "#92400e" }}>
          <strong>Setup Required:</strong> Run the valuation_snapshots migration in Supabase SQL Editor to enable data persistence. The dashboard works with calculated values in the meantime.
        </div>
      )}
    </div>
  );
}
